import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BenefitType, ReferentPriceType, USState } from '@strata/constants';
import { CurrentBid } from '@strata/react-current-bid';

import { ReferralValueCase } from 'store/api/performanceApi';

export interface SocketLiveReferral {
    referral_id: string;
    payer_id: string;
    segment_id: string;
    product_service_oid: string;
    product_service_description: string;
    product_qty: string;
    member_city: string;
    member_state: USState;
    member_zip: string;
    member_age: number;
    prescriber_id: string;
    default_rate: string;
    referent_price_type: ReferentPriceType;
    price_is_frozen: boolean;
    price_source: string;
    incentive_total?: number;
    dx_name: string;
    prescriber_name: string;
    health_plan: string;
    segment_name: string;
    rx_refillcount: number;
    strength: string;
    place_of_service: string;
    value_cases: ReferralValueCase[];
    referral_expiration: string;
    benefit: BenefitType;
    is_brand: boolean;
    friendly_id: string;
    auction_end: string;
}

export interface LiveReferralSliceState {
    openAuctions: SocketLiveReferral[];
    winningBids: Record<
        string,
        {
            currentBidAmt: string;
            currentBidState: (typeof CurrentBid.states)[keyof typeof CurrentBid.states];
            maxBid?: number;
            tied: boolean;
        }
    >;
    bidOnReferrals: string[];
    liveReferralDataLoaded: boolean;
}

const initialState: LiveReferralSliceState = {
    openAuctions: [],
    winningBids: {},
    bidOnReferrals: [],
    liveReferralDataLoaded: false,
};

export const liveReferralsSlice = createSlice({
    name: 'liveReferrals',
    initialState,
    reducers: {
        updateAuctions(state, action: PayloadAction<SocketLiveReferral[]>) {
            const newAuctions = action.payload;
            return {
                ...state,
                liveReferralDataLoaded: true,
                openAuctions: [
                    // upsert existing auctions
                    ...state.openAuctions.map(
                        (auction) =>
                            newAuctions.find(
                                (newAuction) => newAuction.referral_id === auction.referral_id,
                            ) ?? auction,
                    ),
                    // insert new auctions
                    ...newAuctions.filter(
                        (newAuction) =>
                            !state.openAuctions.find(
                                (openAuction) => openAuction.referral_id === newAuction.referral_id,
                            ),
                    ),
                ],
            };
        },
        setBids(
            state,
            action: PayloadAction<Pick<LiveReferralSliceState, 'winningBids' | 'bidOnReferrals'>>,
        ) {
            const { winningBids, bidOnReferrals } = action.payload;
            return { ...state, winningBids, bidOnReferrals };
        },
    },
});

export const { updateAuctions, setBids } = liveReferralsSlice.actions;
